import { addDays, isIsoDate } from './sales-dates.js';
import { daysInclusive, endOfMonth, shiftMonths, startOfMonth, startOfQuarter, startOfYear } from './sales-periods.js';
/**
 * Sales targets and pace.
 *
 * A target is a figure for one metric, one calendar period and one scope.
 * Pace compares what has sold so far with what should have sold by now for
 * the period to land on target — "should have" weighted by how sales usually
 * fall across the period (weekends, the run-up to a holiday), not a straight
 * line, so a quiet Sunday never reads as falling behind.
 *
 * Periods are calendar months, quarters and years.
 */
export const TARGET_METRICS = ['net_sales', 'orders', 'units'];
export const TARGET_PERIOD_TYPES = ['month', 'quarter', 'year'];
export const TARGET_PERIOD_LABELS = { month: 'Month', quarter: 'Quarter', year: 'Year' };
export function parseTargetScope(key) {
    if (key === 'combined')
        return { kind: 'combined' };
    if (key === 'site:mlf' || key === 'site:mff')
        return { kind: 'site', site: key.slice(5) };
    if (key.startsWith('channel:') && key.length > 8 && key.length <= 60)
        return { kind: 'channel', channelId: key.slice(8) };
    return null;
}
export const isTargetScopeKey = (value) => typeof value === 'string' && parseTargetScope(value) !== null;
/** The first day of the period of this type containing `date`. */
export function periodStartFor(type, date) {
    switch (type) {
        case 'month':
            return startOfMonth(date);
        case 'quarter':
            return startOfQuarter(date);
        case 'year':
            return startOfYear(date);
    }
}
/** Whether `date` is where a period of this type begins — a target must name a real period. */
export function isPeriodStart(type, date) {
    return isIsoDate(date) && periodStartFor(type, date) === date;
}
const MONTHS = { month: 1, quarter: 3, year: 12 };
export function periodRange(type, start) {
    return { from: start, to: endOfMonth(shiftMonths(start, MONTHS[type] - 1)) };
}
export function nextPeriodStart(type, start) {
    return shiftMonths(start, MONTHS[type]);
}
/** A short name for a period: "Sep 2026", "Q3 2026", "2026". */
export function periodLabel(type, start) {
    const year = start.slice(0, 4);
    const month = Number(start.slice(5, 7));
    if (type === 'year')
        return year;
    if (type === 'quarter')
        return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
    return `${MONTH_SHORT[month - 1]} ${year}`;
}
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** The months inside a quarter or year, for adding up monthly targets. */
export function monthsIn(type, start) {
    return Array.from({ length: MONTHS[type] }, (_, i) => shiftMonths(start, i));
}
/**
 * The target for a period: the one set for it, or for a quarter or year with
 * none set, the sum of its months — but only when every month has one, since
 * a sum with a gap would be a target nobody chose.
 */
export function resolveTarget(targets, metric, type, start, scopeKey) {
    const match = (t, pt, ps) => t.metric === metric && t.scopeKey === scopeKey && t.periodType === pt && t.periodStart === ps;
    const explicit = targets.find((t) => match(t, type, start));
    if (explicit)
        return { value: explicit.value, source: 'set' };
    if (type === 'month')
        return null;
    let sum = 0;
    for (const month of monthsIn(type, start)) {
        const found = targets.find((t) => match(t, 'month', month));
        if (!found)
            return null;
        sum += found.value;
    }
    return { value: sum, source: 'months' };
}
// ---- pace -------------------------------------------------------------------
/**
 * The share of a period's sales normally done by the end of `through`
 * (inclusive), from a weight per day of the period. With no usable weights,
 * a straight line.
 */
export function expectedShareThrough(range, through, weights) {
    if (through < range.from)
        return 0;
    if (through >= range.to)
        return 1;
    const total = daysInclusive(range);
    if (!weights)
        return daysInclusive({ from: range.from, to: through }) / total;
    let all = 0;
    let done = 0;
    for (let d = range.from, i = 0; d <= range.to && i < 400; d = addDays(d, 1), i++) {
        const w = Math.max(0, weights.get(d) ?? 0);
        all += w;
        if (d <= through)
            done += w;
    }
    return all > 0 ? done / all : daysInclusive({ from: range.from, to: through }) / total;
}
/**
 * Whether a period is heading for its target.
 *
 * Judged on the projection when there is one — a forecast knows the busy
 * part of the month is still to come — and otherwise on pace so far. Too
 * early in a period (under 10% elapsed) everything reads as on track: two
 * days' sales say nothing about thirty.
 */
export function computePace(input, remainingDays) {
    const { target, actual, expectedShare, forecast } = input;
    if (target === null || target <= 0) {
        return {
            target: null,
            actual,
            expectedToDate: null,
            paceRatio: null,
            projected: forecast,
            projectedRatio: null,
            neededPerDay: null,
            status: 'no_target',
        };
    }
    const expectedToDate = target * expectedShare;
    const paceRatio = expectedToDate > 0 ? actual / expectedToDate : null;
    const projected = forecast ?? (expectedShare > 0 ? actual / expectedShare : null);
    const projectedRatio = projected === null ? null : projected / target;
    const neededPerDay = remainingDays > 0 ? Math.max(0, target - actual) / remainingDays : null;
    let status = 'on_track';
    const ratio = projectedRatio ?? paceRatio;
    if (input.elapsedShare >= 0.1 && ratio !== null) {
        if (ratio >= 1.1)
            status = 'ahead';
        else if (ratio < 0.9)
            status = 'at_risk';
        else if (ratio < 0.98)
            status = 'behind';
    }
    if (actual >= target)
        status = 'ahead';
    return { target, actual, expectedToDate, paceRatio, projected, projectedRatio, neededPerDay, status };
}
//# sourceMappingURL=sales-targets.js.map